import React from "react";
import moment from "moment";
import clsx from "clsx";
import _ from "lodash";

import Grid from "@material-ui/core/Grid";

import BaseDialog from "../../../../../../../../../components/BaseDialog/BaseDialog";
import {NameColNew} from "../../../ProductsSelectionSteps/components/InvestmentRecommendationStep/components/Portfolio/table-data";
import WarningTooltip from "../../../../../../../../../components/WarningTooltip";
import {
  END_DATE_DAY_SHOULD_EQ_START,
  END_DATE_SHOULD_BE_AFTER_START
} from "../../../../../../../../Trades/constants";
import CalendarFormElement from "../../../../formElement/CalendarFormElement";
import SelectFormElement from "../../../../formElement/SelectFormElement";
import PrimaryButton from "../../../../../../../../../components/Buttons/PrimaryButton";
import {
  getBankData,
  getListOptionsFromBankData,
  handleFromDateValidity,
  isRequiredQuestion
} from "../../../../../../../utils";
import {DATE_FORMAT} from "../../../../../../../../../utils/constants";
import {DateQuestionMethods} from "../../../../question/DateQuestion";
import {SelectQuestionMethods} from "../../../../question/SelectQuestion";
import {newDesignInputCustomClasses} from "../../../../../../../constants";
import CheckboxFormElement from "../../../../formElement/CheckboxFormElement";

import useStyles from "./styles";

const START_DATE_DISABLED_MSG = "Die Depotbank unterstützt kein in der Zukunft liegendes Datum. Die Änderung " +
  "wird automatisch zum nächstmöglichen Zeitpunkt vorgenommen";
const MIN_FROM_DATE = moment().add(1,'days');
const _formatDate = (value) => value ? moment(value).format(DATE_FORMAT) : value;

const _toMoment = (value) => value ? moment(value, DATE_FORMAT) : value;

const _convertToForm = (plan) => ({
  rotation: plan.rotation,
  rotation_error: plan.rotation_error,
  from_date: _toMoment(plan.from_date),
  from_date_error: plan.from_date_error,
  till_date: _toMoment(plan.till_date),
  till_date_error: plan.till_date_error,
  populate: false,
});

const _convertFromForm = (plan) => ({
  rotation: plan.rotation,
  rotation_error: false,
  from_date: _formatDate(plan.from_date),
  from_date_error: false,
  till_date: _formatDate(plan.till_date),
  till_date_error: false,
});

const SavingPlanSettingsForOnboarding = (props) => {
  const {
    item,
    banksData,
    bankCompanyId,
    onChange,
    hasMultipleSavingsPlans,
  } = props;

  const bankData = getBankData(banksData, bankCompanyId);
  const endDateDisabled = !bankData.onboarding_savings_plan_till_date_enabled;

  const handleDateValidity = (date) => handleFromDateValidity(
        banksData, bankCompanyId, 'savings_plan_execution_dates', date);
  const rotationOptions = getListOptionsFromBankData(banksData, bankCompanyId);
  const currentPlan = item.savingPlanData || {};

  return (
    <SavingPlanSettings
      currentPlan={currentPlan}
      item={item}
      onChange={onChange}
      hasMultipleSavingsPlans={hasMultipleSavingsPlans}
      rotationOptions={rotationOptions}
      handleDateValidity={handleDateValidity}
      endDateDisabled={endDateDisabled}
      convertFromForm={_convertFromForm}
    />
  )

}

export const SavingPlanSettings = (
  {
    currentPlan,
    item,
    onChange,
    hasMultipleSavingsPlans,
    rotationOptions,
    handleDateValidity,
    rotationDisabled=false,
    fromDateDisabled=false,
    endDateDisabled=false,
    convertFromForm,
    customClasses={},
    modalTitle='Sparplan einrichten'
  }) => {
  const componentClasses = useStyles();
  const [settingsModalOpen, setSettingsModalOpen] = React.useState(false);
  const [formData, setFormData] = React.useState(_convertToForm(currentPlan));
  const rotationMap = React.useMemo(
    () => _.keyBy(rotationOptions, 'value'),
  [rotationOptions]
  )

  const handleClose = () => {
    setSettingsModalOpen(false);
    setFormData(_convertToForm(currentPlan)); // cancel changes
  };
  const onOpen = () => {
    setFormData(_convertToForm(currentPlan)); // init
    setSettingsModalOpen(true)
  };

  const handleSave = () => {
    const errors = {};

    // validate rotation
    if(!SelectQuestionMethods.valid(rotationQ)){
      errors.rotation_error = rotationQ.error
    }
    // validate start date
    if(!DateQuestionMethods.valid(startDateQ)){
      errors.from_date_error = startDateQ.error
    } else if (endDateQ.answer){
      // validate end date
      if(!DateQuestionMethods.valid(endDateQ)){
        errors.end_date_error = endDateQ.error;
      }
    }

    if(!_.isEmpty(errors)){
      setFormData({...formData, ...errors});
      return false;
    }

    onChange(item, convertFromForm(formData), hasMultipleSavingsPlans && formData.populate);
    setSettingsModalOpen(false);
  };

  const handleEndDateValidity = React.useCallback((date) => {
    // in case fromDate is set -> endDate should use "same day"
    return !!formData.from_date ? moment(date).date() !== moment(formData.from_date).date() : handleDateValidity(date)
  }, [formData.from_date]);

  const hasError = currentPlan.rotation_error || currentPlan.from_date_error || currentPlan.till_date_error;

  const rotationQ = {
    optional: false,
    answer: _.get(formData.rotation, 'value'),
  };

  const startDateQ = {
    optional: false,
    answer: formData.from_date,
    min_date: MIN_FROM_DATE,
    handleDateValidity: handleDateValidity,
  };

  const endDateQ = {
    answer: formData.till_date,
    min_date: moment(startDateQ.answer).add(1,'days'),
    min_date_msg: END_DATE_SHOULD_BE_AFTER_START,
    handleDateValidity: handleEndDateValidity
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={true}>
        <Grid container>
          <Grid item xs={12}>
            <span className={clsx(componentClasses.label, customClasses.rotation, currentPlan.rotation_error && 'error')}>
              {currentPlan.rotation.label}
            </span>
          </Grid>
          {!!currentPlan.from_date && (
            <Grid item xs={12}>
              <span className={clsx(componentClasses.label, customClasses.from_date, currentPlan.from_date_error && 'error')}>
                ab {currentPlan.from_date}
              </span>
            </Grid>
          )}
          {!!currentPlan.till_date && (
            <Grid item xs={12}>
              <span className={clsx(componentClasses.label, customClasses.till_date, currentPlan.till_date_error && 'error')}>
                bis {currentPlan.till_date}
              </span>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={'auto'}>
        <i className={clsx('far fa-calendar-edit', componentClasses.icon, hasError && 'error')} onClick={onOpen} />
      </Grid>
      <BaseDialog
        open={settingsModalOpen}
        handleClose={handleClose}
        title={modalTitle}
        maxWidth={'sm'}
        actions={
          <Grid container justify={"flex-end"} spacing={2}>
            <Grid item xs={12} sm={'auto'}>
              <PrimaryButton
                text="Abbrechen"
                variant="outlined"
                onButtonClick={handleClose}
              />
            </Grid>
            <Grid item xs={12} sm={'auto'}>
              <PrimaryButton text={"Speichern"} onButtonClick={handleSave} />
            </Grid>
          </Grid>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div>Für Order:</div>
            <div>{NameColNew.body[0]({item: item.data || item, options: {hideAcceptanceCriteriasUnderName: true}})}</div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectFormElement
              label={'Turnus'}
              value={rotationQ.answer || ''}
              options={rotationOptions || []}
              disabled={rotationDisabled}
              onChange={(value) => setFormData({...formData, rotation: rotationMap[value], rotation_error: null})}
              required={isRequiredQuestion(rotationQ)}
              error={formData.rotation_error}
              custom_classes={newDesignInputCustomClasses}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CalendarFormElement
              label={<>Einzahlungen ab{fromDateDisabled && <WarningTooltip title={START_DATE_DISABLED_MSG} />}</>}
              value={startDateQ.answer}
              disabled={fromDateDisabled}
              onChange={(value) => setFormData({...formData, from_date: value, from_date_error: null})}
              required={isRequiredQuestion(startDateQ)}
              minDate={startDateQ.min_date}
              disableDatesCallback={startDateQ.handleDateValidity}
              error={formData.from_date_error}
              custom_classes={newDesignInputCustomClasses}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CalendarFormElement
              label={(<>
                <span>Letzte Ausführung</span>
                <WarningTooltip title={endDateDisabled ? "Bei dieser Depotbank nicht verfügbar" : END_DATE_DAY_SHOULD_EQ_START} />
              </>
              )}
              value={endDateQ.answer}
              onChange={(value) => setFormData({...formData, till_date: value, end_date_error: null})}
              required={isRequiredQuestion(endDateQ)}
              disabled={endDateDisabled}
              minDate={endDateQ.min_date}
              minError={endDateQ.min_date_msg}
              disableDatesCallback={endDateQ.handleDateValidity}
              error={formData.end_date_error}
              custom_classes={newDesignInputCustomClasses}
            />
          </Grid>
          {hasMultipleSavingsPlans && (
            <Grid item xs={12}>
              <CheckboxFormElement
                label={'Auswahl für alle Sparplan-Order übernehmen'}
                value={formData.populate}
                onChange={(val) => setFormData({...formData, populate: val})}
                custom_classes={{label: componentClasses.checkboxLabel}}
              />
            </Grid>
          )}
        </Grid>
      </BaseDialog>
    </Grid>
  )
};

export default SavingPlanSettingsForOnboarding;